'use client'

import * as CollapsiblePrimitive from '@radix-ui/react-collapsible'
import { Slot } from '@radix-ui/react-slot'
import {
  Bell,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  LogOut,
  Monitor,
  Moon,
  Plus,
  Sun,
  User as UserIcon,
} from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { setAppBarCurrentAppId, useAppBarState } from '../app-bar-state'
import { APP_CHROME_HEIGHT_CLASS } from '../app-chrome'
import { appIconStyle } from '../app-icon-style'
import { cn } from '../cn'
import { useAppFavicon } from '../hooks/use-app-favicon'
import { type ColorMode, isColorMode } from '../hooks/use-dark-mode'
import { useOrdoApps } from '../hooks/use-ordo-apps'
import { revealDragCloses, revealDragOffset, useShellChrome, useShellLayout, useSidebarState } from './app-shell'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Sheet, SheetContent, SheetTitle } from './sheet'
import { Switch } from './switch'
import { TeamCircle } from './team-circle'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'
import { type ViewOption, ViewToggle } from './view-toggle'

interface AppSidebarContextValue {
  onNavigate?: () => void
  /** Scrolls the body region (everything between the pinned header and footer)
   *  back to the top. Wired by `AppSidebar`; consumed by the app switcher so
   *  toggling the brand row also resets the user's scroll position — clicking
   *  the top of the sidebar returns you to the top of the sidebar. */
  scrollBodyToTop?: () => void
  /**
   * When true, child primitives render in icon-only mode and reveal their
   * label as a Radix tooltip on hover. Provided by `AppSidebar` and read by
   * `AppSidebarItem`, `AppSidebarSection`, `AppSidebarAppSwitcher`, and
   * `AppSidebarUserMenu` (and anything composed from them).
   */
  collapsed?: boolean
  /** True while the horizontal `AppBar` in the top bar is showing and owns
   *  app switching (desktop only). The header switcher then drops its
   *  chevron and `AppSidebarAppsList` renders nothing — the bar already
   *  lists every app. */
  appBar?: boolean
}

const AppSidebarContext = React.createContext<AppSidebarContextValue>({})

/**
 * Whether the top bar's `AppBar` replaces the sidebar's expandable app list:
 * only on desktop, only for switcher-headed sidebars, only when the host
 * hasn't opted out, and only while the bar is actually showing (every icon
 * fits). Exported for tests.
 */
export function appBarOwnsSwitching(args: {
  isDesktop: boolean
  headerIsSwitcher: boolean
  appBar: boolean | undefined
  barShowing: boolean
}): boolean {
  return args.isDesktop && args.headerIsSwitcher && args.appBar !== false && args.barShowing
}

/**
 * Unified shell: whether app switching lives outside this sidebar. On desktop
 * that's the top bar's app row (only while it fits — otherwise the sidebar
 * keeps its switcher as a fallback); in the phone drawer it's always
 * `MobileTopRow`'s app button. When true the sidebar drops its app-name
 * header and apps list. Exported for tests.
 */
export function unifiedSwitchingElsewhere(args: {
  unified: boolean
  isDesktop: boolean
  headerIsSwitcher: boolean
  barOwnsSwitching: boolean
}): boolean {
  if (!args.unified || !args.headerIsSwitcher) {
    return false
  }
  return args.isDesktop ? args.barOwnsSwitching : true
}

function useAppSidebarCollapsed(): boolean {
  return React.useContext(AppSidebarContext).collapsed ?? false
}

export interface AppSidebarProps extends React.HTMLAttributes<HTMLElement> {
  variant?: 'desktop' | 'drawer'
  onNavigate?: () => void
  /**
   * Render the desktop sidebar in icon-only mode at a fixed narrow width.
   * Labels move into Radix tooltips that reveal on hover. The drawer
   * variant ignores this prop (mobile drawer is always full-label).
   */
  collapsed?: boolean
  /**
   * Hand app switching to the horizontal `AppBar` in the top bar
   * (PROD-1403) while it is showing. Default on; pass `false` to always keep
   * the legacy click-to-expand apps list. The drawer variant always keeps it.
   */
  appBar?: boolean
  /** Current app id for a host with a plain `AppSidebarBrand` header (Ordo),
   *  so the top bar's `AppBar` can mark it. Switcher hosts pass it on
   *  `AppSidebarAppSwitcher`. */
  appBarCurrentAppId?: string
}

/**
 * The canonical sidebar font. Inlined so it takes precedence over per-app
 * `--font-sans` overrides — sidebars must render in Inter across every app
 * regardless of the app's body font.
 */
const SIDEBAR_FONT_STACK = "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"

// ------------------------------------------------------------------
// Sidebar look — the ONE place it is defined. Tasks (apps/tasker) is the
// reference design; every app gets this look by using the primitives, with
// no typography, spacing or icon-size classes of its own. Each value is
// pinned here rather than read from the host's theme, because the hosts'
// themes disagree (radius scale, font smoothing) and the sidebar must not.
// ------------------------------------------------------------------

/**
 * Text rendering. `@ordo/ui/theme` (and boombox, rfp-gremlin) switch the page
 * to grayscale `antialiased` smoothing, which draws the sidebar's text visibly
 * thinner on macOS than Tasks, which leaves smoothing at the browser default.
 * Pinning `auto` here makes every sidebar draw Tasks' weight.
 */
const SIDEBAR_TEXT_RENDERING = 'subpixel-antialiased'

/**
 * Corner radius of every row's hover/active fill and every tile. Tasks'
 * `rounded-md` is 6px; hosts on `@ordo/ui/theme` resolve `rounded-md` to 8px
 * and HQ to 6.4px, so the value is fixed instead of read from `--radius-md`.
 */
const SIDEBAR_RADIUS = 'rounded-[6px]'

/**
 * The section header ("ADMINISTRATION", "TEAMS", "STARRED", …). Every label,
 * collapsible or not, renders through this.
 */
const SIDEBAR_SECTION_LABEL = 'text-sm md:text-xs font-medium text-muted-foreground uppercase tracking-wider'

/**
 * The leading icon slot of a row. Hosts pass a bare icon (`<Settings />`);
 * the slot sizes a direct `<svg>` child to 20px on phones and 16px from `md`,
 * so no app sizes its own sidebar icons. Non-svg leading content (a colored
 * team dot, a project badge, an `AppSidebarTile`) keeps its own size.
 */
const SIDEBAR_ICON_SLOT =
  'inline-flex shrink-0 items-center justify-center [&>svg]:h-5 [&>svg]:w-5 md:[&>svg]:h-4 md:[&>svg]:w-4 [&>svg]:shrink-0'

/** Left inset of a nested row (Tasks' boards under a team): icon column of
 *  the parent row's label. */
const SIDEBAR_INSET_PL = 'pl-7'

/**
 * The look above is for the internal tools, which all mount
 * `<AppShell layout="unified">`. A classic shell is apps/ordo, the
 * customer-facing app, and its sidebar stays exactly as it was built: the
 * host's own `rounded-md`, its own icon sizes, its own font smoothing and
 * spacing. Every piece of the unified look is gated on this, and
 * `tests/app-shell-layout.test.tsx` pins the classic classes.
 */
const useUnifiedLook = () => useShellLayout() === 'unified'
const sidebarRadius = (unified: boolean) => (unified ? SIDEBAR_RADIUS : 'rounded-md')

/**
 * Mobile drawer width. `w-full` fills the available width up to a 320px cap,
 * so on phones narrower than 320px (e.g. iPhone SE in landscape) the drawer
 * shrinks to fit instead of overflowing. This is the single source of truth —
 * every app renders its drawer through `AppSidebarDrawer`, so changing this
 * constant propagates to every consumer in one shot.
 */
const SIDEBAR_DRAWER_WIDTH_CLASS = 'w-full max-w-[320px]'

// Fixed widths. 260px is the long-standing default; 56px is the icon-rail
// width, sized to fit a `h-9 w-9` hit target with 4px symmetric padding.
const SIDEBAR_WIDTH_CLASS = 'md:w-[260px]'
const SIDEBAR_COLLAPSED_WIDTH_CLASS = 'md:w-[56px]'

// ------------------------------------------------------------------
// App-switcher icon column — numbers first, classes derived, so the
// trigger row and the app rows land their tile's LEFT EDGE on the exact
// same x. (PR #1485 attempt pinned constant USAGE but got the arithmetic
// wrong by omitting AppSidebarNav's own container padding — the rows sat
// 8px right of the trigger on prod.)
//
// Effective left offset of a tile = SUM of every horizontal contributor
// between the sidebar's left edge and the tile:
//
//   trigger: (header has no padding) + brandInner SWITCHER_TRIGGER_PX
//          = 16px
//   rows:    AppSidebarNav SIDEBAR_NAV_PX (every host wraps the list in
//            AppSidebarNav — hq/tasker/agent/boombox/customers/menus/
//            factory/previews/rfp-gremlin/road-warrior)
//          + apps <ul> SWITCHER_LIST_PX
//          + AppSidebarItem SWITCHER_ITEM_PX (its own 'px-2')
//          = 8 + 0 + 8 = 16px
//
// The classes below are DERIVED from these numbers via TAILWIND_PX — edit
// the numbers, never the classes. `computeTriggerTileLeft()` and
// `computeRowTileLeft()` expose the same arithmetic to the geometry test,
// which fails whenever the two sums diverge.
// ------------------------------------------------------------------
export const SIDEBAR_NAV_PX = 8 // AppSidebarNav container padding ('px-2')
export const SWITCHER_TRIGGER_PX = 16 // brandInner padding
export const SWITCHER_LIST_PX = 0 // apps <ul> padding
export const SWITCHER_ITEM_PX = 8 // AppSidebarItem's own 'px-2' (appSidebarItemClasses)

const TAILWIND_PX: Record<number, string> = { 0: 'px-0', 8: 'px-2', 16: 'px-4' }

export const computeTriggerTileLeft = (): number => SWITCHER_TRIGGER_PX
export const computeRowTileLeft = (): number => SIDEBAR_NAV_PX + SWITCHER_LIST_PX + SWITCHER_ITEM_PX

export const SWITCHER_TILE_SIZE = 'md' as const
export const SWITCHER_TILE_IMG_CLASS = cn('h-7 w-7', SIDEBAR_RADIUS)
export const SWITCHER_TRIGGER_INSET_CLASS = TAILWIND_PX[SWITCHER_TRIGGER_PX]!
export const SWITCHER_LIST_INSET_CLASS = TAILWIND_PX[SWITCHER_LIST_PX]!

/**
 * The one leading-tile renderer for the switcher surface. Both the trigger
 * row (current app) and every app row resolve through this: full-bleed
 * `<img>` when the registry supplies an admin-managed tile SVG, else an
 * `AppSidebarTile` (brand colors when available) — always the same box.
 */
const renderSwitcherTile = (
  app: AppSwitcherApp | undefined,
  fallback: { icon: React.ReactNode; colorKey?: string },
): React.ReactNode => {
  if (app?.logo) {
    // A real brand tile (Ordo's "o" app icon) — same box as the image path.
    return (
      <span
        aria-hidden
        className={cn(
          'inline-flex overflow-hidden ring-1 ring-border [&>svg]:h-full [&>svg]:w-full',
          SWITCHER_TILE_IMG_CLASS,
        )}>
        {app.logo}
      </span>
    )
  }
  if (app?.image) {
    return <img src={app.image} alt="" className={SWITCHER_TILE_IMG_CLASS} />
  }
  const useBrand = !!app?.bgColor && !!app?.fgColor
  return (
    <AppSidebarTile
      icon={app?.icon ?? fallback.icon}
      bgColor={app?.bgColor}
      fgColor={app?.fgColor}
      colorKey={useBrand ? undefined : (fallback.colorKey ?? 'gray')}
      size={SWITCHER_TILE_SIZE}
    />
  )
}

// Three-row layout: a pinned header (brand or app-switcher), a scrollable body,
// and a pinned footer. Identifying the header/footer by displayName means the
// host can write the children in natural reading order — we just sort them
// into the right slot. We deliberately do NOT use `position: sticky` for
// pinning: iOS Safari has long-standing bugs where sticky elements inside an
// `overflow: auto` ancestor flicker, drop their borders during momentum scroll,
// and lose their backing color. Real flex layout — header `shrink-0`, body
// `flex-1 overflow-y-auto`, footer `shrink-0` — is reliable everywhere.
const HEADER_DISPLAY_NAMES = new Set(['AppSidebarBrand', 'AppSidebarAppSwitcher'])

const AppSidebar = React.forwardRef<HTMLElement, AppSidebarProps>(
  (
    {
      variant = 'desktop',
      onNavigate,
      collapsed = false,
      appBar,
      appBarCurrentAppId,
      className,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const isDesktop = variant === 'desktop'
    // Drawer mode never collapses — phones don't have a "rail" mode, the
    // drawer slides in fully labeled.
    const isCollapsed = isDesktop && collapsed

    // Shared open state for the apps list. The chevron in
    // `AppSidebarAppSwitcher`'s brand row (Trigger) and the `<ul>` of apps
    // in `AppSidebarAppsList` (Content) coordinate through a single Radix
    // `Collapsible.Root` here so they can sit in different DOM regions —
    // brand pinned in the header, apps list in the scrollable body — and
    // still toggle together. Always starts closed on a fresh mount: any
    // full page load (cross-app navigation, refresh) is "you just landed
    // here, you weren't browsing apps." React state still survives SPA
    // route changes within the same app because the component stays
    // mounted, so opening the switcher and clicking around inside the
    // current app keeps it open — only a hard reload resets to closed.
    const [appsOpen, setAppsOpen] = React.useState<boolean>(false)
    const handleAppsOpenChange = React.useCallback((next: boolean) => {
      setAppsOpen(next)
    }, [])
    const bodyRef = React.useRef<HTMLDivElement>(null)
    const scrollBodyToTop = React.useCallback(() => {
      bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }, [])
    const childArray = React.Children.toArray(children)
    const headerIndex = childArray.findIndex(
      (c) =>
        React.isValidElement(c) && HEADER_DISPLAY_NAMES.has((c.type as { displayName?: string }).displayName ?? ''),
    )
    const footerIndex = childArray.findIndex(
      (c) => React.isValidElement(c) && (c.type as { displayName?: string }).displayName === 'AppSidebarFooter',
    )
    const header = headerIndex >= 0 ? childArray[headerIndex] : null
    const footer = footerIndex >= 0 ? childArray[footerIndex] : null
    const body = childArray.filter((_, i) => i !== headerIndex && i !== footerIndex)
    // The shared `Collapsible.Root` is here so the chevron in
    // `AppSidebarAppSwitcher` and the `<ul>` in `AppSidebarAppsList` can
    // coordinate across the pinned-header/scroll-body boundary. Hosts that
    // don't render a chevron (HQ uses `collapsible={false}`, or hosts that
    // render `AppSidebarBrand` instead of the switcher) have no UI to
    // toggle the Root, so the user-facing default of "closed" would lock
    // their apps list permanently hidden. Force the Root open in that
    // case — the localStorage-persisted `appsOpen` only governs hosts
    // that actually expose a toggle.
    const headerIsCollapsibleSwitcher =
      React.isValidElement(header) &&
      (header.type as { displayName?: string }).displayName === 'AppSidebarAppSwitcher' &&
      (header.props as { collapsible?: boolean }).collapsible !== false
    const effectiveOpen = headerIsCollapsibleSwitcher ? appsOpen : true
    const headerIsSwitcher =
      React.isValidElement(header) && (header.type as { displayName?: string }).displayName === 'AppSidebarAppSwitcher'
    // The top bar's AppBar marks the current app; the sidebar is where every
    // host already declares it, so publish it from here.
    const currentAppId = headerIsSwitcher
      ? (header.props as { currentAppId?: string }).currentAppId
      : appBarCurrentAppId
    // The unified shell (opt-in, internal tools) moves app switching to the
    // top bar on desktop and to `MobileTopRow` on phones.
    const unified = useShellLayout() === 'unified'
    React.useEffect(() => {
      // The mobile top row reads it too, so the unified shell publishes from
      // the drawer as well as the (CSS-hidden on phones) desktop sidebar.
      if ((isDesktop || unified) && currentAppId) {
        setAppBarCurrentAppId(currentAppId)
      }
    }, [isDesktop, unified, currentAppId])
    // Tab favicon from the HQ registry entry (icon + colors set in HQ → Apps).
    useAppFavicon(isDesktop ? currentAppId : undefined)
    const { barShowing } = useAppBarState()
    const barOwnsSwitching = appBarOwnsSwitching({ isDesktop, headerIsSwitcher, appBar, barShowing })
    const switchingElsewhere = unifiedSwitchingElsewhere({ unified, isDesktop, headerIsSwitcher, barOwnsSwitching })
    const value = React.useMemo<AppSidebarContextValue>(
      () => ({ onNavigate, scrollBodyToTop, collapsed: isCollapsed, appBar: barOwnsSwitching || switchingElsewhere }),
      [onNavigate, scrollBodyToTop, isCollapsed, barOwnsSwitching, switchingElsewhere],
    )
    // Unified shell: the app is already named where you switch it — the top
    // bar's app row on desktop, `MobileTopRow`'s app button on phones — so the
    // sidebar's app-name header goes away and the nav starts at Home.
    const visibleHeader = switchingElsewhere ? null : header
    const aside = (
      <aside
        ref={ref}
        data-collapsed={isCollapsed ? 'true' : undefined}
        className={cn(
          isDesktop
            ? cn(
                // `transition-[width]` animates the collapse/expand swap
                // between rail (56px) and full (260px). `h-full`, NOT
                // `h-screen`: the sidebar fills the shell ROW it sits in.
                // Inside the standard `h-dvh` AppShellRoot that is the
                // viewport, but a host that stacks a banner above the
                // row (ordo's impersonation + announcement bars) hands
                // the row `viewport - banner`, and a viewport-tall
                // sidebar there is clipped by the host's overflow —
                // the footer (sign out) fell below the fold.
                'hidden md:flex md:flex-col md:border-r md:border-sidebar-border bg-sidebar h-full sticky top-0 transition-[width] duration-200 ease-out',
                isCollapsed ? SIDEBAR_COLLAPSED_WIDTH_CLASS : SIDEBAR_WIDTH_CLASS,
              )
            : cn(
                'flex w-full flex-col bg-sidebar h-dvh',
                // Header-less unified drawer: keep Home clear of the status bar
                // when installed to the home screen (viewport-fit=cover).
                switchingElsewhere && 'pt-[env(safe-area-inset-top,0px)]',
              ),
          unified && SIDEBAR_TEXT_RENDERING,
          className,
        )}
        style={{
          fontFamily: SIDEBAR_FONT_STACK,
          ...style,
        }}
        {...props}>
        {visibleHeader}
        <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto">
          {body}
        </div>
        {footer}
      </aside>
    )
    return (
      <AppSidebarContext.Provider value={value}>
        <TooltipProvider delayDuration={150}>
          <CollapsiblePrimitive.Root open={effectiveOpen} onOpenChange={handleAppsOpenChange}>
            {aside}
          </CollapsiblePrimitive.Root>
        </TooltipProvider>
      </AppSidebarContext.Provider>
    )
  },
)
AppSidebar.displayName = 'AppSidebar'

export interface AppSidebarBrandProps extends React.HTMLAttributes<HTMLDivElement> {
  href?: string
  asChild?: boolean
}

const AppSidebarBrand = React.forwardRef<HTMLDivElement, AppSidebarBrandProps>(
  ({ href, asChild, className, children, ...props }, ref) => {
    const brandClasses = cn(
      'flex items-center gap-2 px-4 border-b border-sidebar-border shrink-0 text-sidebar-foreground',
      APP_CHROME_HEIGHT_CLASS,
      className,
    )
    if (asChild) {
      return (
        <Slot
          ref={ref as React.Ref<HTMLElement>}
          className={brandClasses}
          {...(props as React.HTMLAttributes<HTMLElement>)}>
          {children}
        </Slot>
      )
    }
    if (href) {
      return (
        <a
          ref={ref as unknown as React.Ref<HTMLAnchorElement>}
          href={href}
          className={brandClasses}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
          {children}
        </a>
      )
    }
    return (
      <div ref={ref} className={brandClasses} {...props}>
        {children}
      </div>
    )
  },
)
AppSidebarBrand.displayName = 'AppSidebarBrand'

const AppSidebarNav = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, children, ...props }, ref) => (
    // No own overflow — `AppSidebar` wraps everything above the footer in a
    // single scroll container, so apps + teams + admin scroll together.
    // Horizontal padding is derived from SIDEBAR_NAV_PX — it participates in
    // the switcher icon-column arithmetic (see computeRowTileLeft).
    <nav ref={ref} className={cn(TAILWIND_PX[SIDEBAR_NAV_PX], 'py-2 space-y-3', className)} {...props}>
      {children}
    </nav>
  ),
)
AppSidebarNav.displayName = 'AppSidebarNav'

export interface AppSidebarDrawerProps {
  /** Override controlled open state. Defaults to AppShell's sidebar context. */
  open?: boolean
  /** Override controlled state setter. Defaults to AppShell's sidebar context. */
  onOpenChange?: (open: boolean) => void
  /** Accessible name for the drawer dialog. Default "Navigation". */
  title?: string
  children: React.ReactNode
}

/**
 * The sr-only `SheetTitle` lives here (not at every call site) so every app's
 * drawer ships with an accessible name by default. Without this, Radix Dialog
 * renders without an a11y label and screen readers announce nothing useful.
 *
 * `open`/`onOpenChange` default to the AppShell sidebar context, so apps just
 * render `<AppSidebarDrawer><AppSidebar /></AppSidebarDrawer>` and the
 * hamburger Just Works. Pass props explicitly to opt out (rare).
 */
const AppSidebarDrawer = ({ open, onOpenChange, title = 'Navigation', children }: AppSidebarDrawerProps) => {
  const sidebarState = useSidebarState()
  const { mobileNav, navRevealed, setNavDrag } = useShellChrome()
  const resolvedOpen = open ?? sidebarState?.open ?? false
  const resolvedOnOpenChange = onOpenChange ?? sidebarState?.setOpen ?? (() => undefined)
  if (mobileNav === 'reveal') {
    return (
      <RevealMenu
        open={resolvedOpen}
        revealed={navRevealed}
        onOpenChange={resolvedOnOpenChange}
        onDrag={setNavDrag}
        title={title}>
        {children}
      </RevealMenu>
    )
  }
  return (
    <Sheet open={resolvedOpen} onOpenChange={resolvedOnOpenChange}>
      <SheetContent side="left" className={cn('p-0', SIDEBAR_DRAWER_WIDTH_CLASS)} hideCloseButton>
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {children}
      </SheetContent>
    </Sheet>
  )
}
AppSidebarDrawer.displayName = 'AppSidebarDrawer'

interface RevealMenuProps {
  open: boolean
  revealed: boolean
  onOpenChange: (open: boolean) => void
  /** Where the app sits while a finger drags the menu closed; null when not dragging. */
  onDrag: (offset: number | null) => void
  title: string
  children: React.ReactNode
}

/**
 * The `reveal` phone menu (`<AppShell mobileNav="reveal">`, PROD-1530). It
 * sits in a fixed layer BEHIND the app surface; `AppShell` slides the app to
 * the right to uncover it, so the current page stays mounted with its scroll
 * and state. The layer is portaled to <body> so it is not inside the moving
 * surface. A transparent button over the exposed edge of the app closes it,
 * as does Escape. Focus moves into the menu on open and back on close.
 */
/** A touch must move this far before it counts as a drag in one direction. */
const REVEAL_DRAG_SLOP_PX = 8

function RevealMenu({ open, revealed, onOpenChange, onDrag, title, children }: RevealMenuProps) {
  const [mounted, setMounted] = React.useState(false)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const layerRef = React.useRef<HTMLDivElement>(null)
  const closeRef = React.useRef<HTMLButtonElement>(null)

  // Dragging right to left on the menu or the exposed app closes it, with the
  // app following the finger. Vertical drags are left to the menu's own scroll.
  React.useEffect(() => {
    if (!open) {
      return
    }
    type Drag = {
      x0: number
      y0: number
      width: number
      mode: 'pending' | 'horizontal' | 'vertical'
      x: number
      t: number
      vx: number
    }
    let drag: Drag | null = null
    const inside = (target: EventTarget | null) =>
      target instanceof Node && Boolean(layerRef.current?.contains(target) || closeRef.current?.contains(target))
    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!touch || event.touches.length > 1 || !inside(event.target)) {
        drag = null
        return
      }
      const width = panelRef.current?.offsetWidth ?? 320
      drag = {
        x0: touch.clientX,
        y0: touch.clientY,
        width,
        mode: 'pending',
        x: touch.clientX,
        t: event.timeStamp,
        vx: 0,
      }
    }
    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!drag || !touch) {
        return
      }
      const dx = touch.clientX - drag.x0
      const dy = touch.clientY - drag.y0
      if (drag.mode === 'pending') {
        if (Math.abs(dx) < REVEAL_DRAG_SLOP_PX && Math.abs(dy) < REVEAL_DRAG_SLOP_PX) {
          return
        }
        drag.mode = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
      }
      if (drag.mode !== 'horizontal') {
        return
      }
      event.preventDefault()
      const dt = Math.max(1, event.timeStamp - drag.t)
      drag.vx = (touch.clientX - drag.x) / dt
      drag.x = touch.clientX
      drag.t = event.timeStamp
      onDrag(revealDragOffset(drag.width, dx))
    }
    const onEnd = () => {
      const finished = drag
      drag = null
      if (!finished || finished.mode !== 'horizontal') {
        return
      }
      const offset = revealDragOffset(finished.width, finished.x - finished.x0)
      onDrag(null)
      if (revealDragCloses({ offset, width: finished.width, velocity: finished.vx })) {
        onOpenChange(false)
      }
    }
    document.addEventListener('touchstart', onStart, { passive: true })
    document.addEventListener('touchmove', onMove, { passive: false })
    document.addEventListener('touchend', onEnd)
    document.addEventListener('touchcancel', onEnd)
    return () => {
      document.removeEventListener('touchstart', onStart)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onEnd)
      document.removeEventListener('touchcancel', onEnd)
      onDrag(null)
    }
  }, [open, onOpenChange, onDrag])

  React.useEffect(() => setMounted(true), [])

  React.useEffect(() => {
    if (!open) {
      return
    }
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    panelRef.current?.focus({ preventScroll: true })
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previous?.focus({ preventScroll: true })
    }
  }, [open, onOpenChange])

  if (!mounted) {
    return null
  }

  return createPortal(
    <>
      <div
        ref={layerRef}
        data-shell-reveal-menu=""
        aria-hidden={open ? undefined : true}
        inert={open ? undefined : true}
        className={cn('fixed inset-0 z-0 touch-pan-y bg-sidebar md:hidden', !revealed && 'invisible')}>
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className="h-dvh w-[min(82vw,320px)] overflow-hidden focus:outline-none">
          {children}
        </div>
      </div>
      {open ? (
        <button
          ref={closeRef}
          type="button"
          aria-label="Close menu"
          onClick={() => onOpenChange(false)}
          className="fixed inset-y-0 left-[min(82vw,320px)] right-0 z-20 cursor-default touch-pan-y md:hidden"
        />
      ) : null}
    </>,
    document.body,
  )
}

// ------------------------------------------------------------------
// Tile (colored rounded-square icon), used by:
//  - AppSidebarItem (tile variant)
//  - AppSidebarAppSwitcher (trigger + dropdown rows)
// ------------------------------------------------------------------

export const SIDEBAR_TILE_COLORS: Record<string, string> = {
  red: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300',
  amber: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300',
  yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-500/15 dark:text-yellow-300',
  green: 'bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-300',
  emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
  teal: 'bg-teal-100 text-teal-600 dark:bg-teal-500/15 dark:text-teal-300',
  cyan: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300',
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
  indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300',
  violet: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-500/15 dark:text-fuchsia-300',
  pink: 'bg-pink-100 text-pink-600 dark:bg-pink-500/15 dark:text-pink-300',
  rose: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',
  slate: 'bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200',
  gray: 'bg-muted text-foreground/70',
}

export const SIDEBAR_TILE_COLORS_SOLID: Record<string, string> = {
  red: 'bg-red-500 text-white',
  orange: 'bg-orange-500 text-white',
  amber: 'bg-amber-500 text-white',
  yellow: 'bg-yellow-500 text-white',
  green: 'bg-green-500 text-white',
  emerald: 'bg-emerald-500 text-white',
  teal: 'bg-teal-500 text-white',
  cyan: 'bg-cyan-500 text-white',
  blue: 'bg-blue-500 text-white',
  indigo: 'bg-indigo-500 text-white',
  violet: 'bg-violet-500 text-white',
  purple: 'bg-purple-500 text-white',
  fuchsia: 'bg-fuchsia-500 text-white',
  pink: 'bg-pink-500 text-white',
  rose: 'bg-rose-500 text-white',
  slate: 'bg-slate-600 text-white',
  gray: 'bg-foreground/60 text-background',
}

export interface AppSidebarTileProps extends React.HTMLAttributes<HTMLSpanElement> {
  colorKey?: string
  /** Arbitrary color (e.g. a user-picked team color). Only used when `variant` is 'solid'. Overrides `colorKey`. */
  color?: string
  /** Hex tile background color (multipass `App.bgColor`). When paired with
   *  `fgColor`, switches the tile to the brand-tile look: 25%-rounded square
   *  in `bgColor` with the icon centered in `fgColor`. Overrides `colorKey`
   *  and `color`. */
  bgColor?: string | null
  /** Hex tile icon color (multipass `App.fgColor`). Required alongside
   *  `bgColor` to activate the brand-tile look. */
  fgColor?: string | null
  size?: 'sm' | 'md' | 'lg' | 'xl'
  icon: React.ReactNode
  /** 'soft' = light tint bg + colored icon (default, for app switcher). 'solid' = saturated bg + white icon (for color+icon group). */
  variant?: 'soft' | 'solid'
}

export const AppSidebarTile = React.forwardRef<HTMLSpanElement, AppSidebarTileProps>(
  (
    { colorKey = 'gray', color, bgColor, fgColor, size = 'sm', icon, variant = 'soft', className, style, ...props },
    ref,
  ) => {
    const unified = useUnifiedLook()
    const sizeCls = size === 'xl' ? 'h-9 w-9' : size === 'lg' ? 'h-8 w-8' : size === 'md' ? 'h-7 w-7' : 'h-6 w-6'
    // Apply the same dimensions to <svg> and <img> children so HQ-served SVG
    // image files render the same size as inline lucide React icons. Without
    // the [&>img] selector, <img> takes its native dimensions and the icon
    // floats off-center inside the tile.
    const iconSize =
      size === 'xl'
        ? '[&>svg]:h-5 [&>svg]:w-5 [&>img]:h-5 [&>img]:w-5'
        : size === 'lg'
          ? '[&>svg]:h-4 [&>svg]:w-4 [&>img]:h-4 [&>img]:w-4'
          : '[&>svg]:h-3.5 [&>svg]:w-3.5 [&>img]:h-3.5 [&>img]:w-3.5'
    // Brand path: multipass-managed App row supplies bg + fg hex. Renders a
    // 25%-rounded square (per the brand spec) with inline colors so any
    // app's branding survives without a code change here.
    const useBrand = !!bgColor && !!fgColor
    const useInlineColor = !useBrand && variant === 'solid' && !!color
    const colorMap = variant === 'solid' ? SIDEBAR_TILE_COLORS_SOLID : SIDEBAR_TILE_COLORS
    const paletteColor = useBrand ? '' : useInlineColor ? 'text-white' : (colorMap[colorKey] ?? colorMap.gray)
    const radiusCls = useBrand ? 'rounded-[25%]' : sidebarRadius(unified)
    const inlineStyle: React.CSSProperties | undefined = useBrand
      ? { ...appIconStyle(bgColor!, fgColor!), ...style }
      : useInlineColor
        ? { backgroundColor: color, ...style }
        : style
    return (
      <span
        ref={ref}
        aria-hidden
        className={cn(
          'inline-flex items-center justify-center shrink-0',
          radiusCls,
          sizeCls,
          iconSize,
          paletteColor,
          className,
        )}
        style={inlineStyle}
        {...props}>
        {icon}
      </span>
    )
  },
)
AppSidebarTile.displayName = 'AppSidebarTile'

// ------------------------------------------------------------------
// Section + show-more
// ------------------------------------------------------------------

export interface AppSidebarSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: React.ReactNode
  labelAsChild?: boolean
  action?: React.ReactNode
  /** If set, truncate the list to N items and render a "Show N more" toggle. */
  maxVisible?: number
  /** Customize the "Show N more" label. Defaults to `Show ${n} more`. */
  moreLabel?: (hidden: number) => string
  /** Customize the "Show less" label. Defaults to `Show less`. */
  lessLabel?: string
}

const AppSidebarSection = React.forwardRef<HTMLDivElement, AppSidebarSectionProps>(
  (
    { label, labelAsChild, action, maxVisible, moreLabel, lessLabel = 'Show less', className, children, ...props },
    ref,
  ) => {
    const [expanded, setExpanded] = React.useState(false)
    const collapsed = useAppSidebarCollapsed()
    const unified = useUnifiedLook()
    const items = React.Children.toArray(children)
    const shouldTruncate = typeof maxVisible === 'number' && items.length > maxVisible
    const visible = shouldTruncate && !expanded ? items.slice(0, maxVisible) : items
    const hidden = shouldTruncate ? items.length - maxVisible! : 0
    // When collapsed, the section header (label + action button) is removed —
    // there's no room for an uppercase label at 56px and most action affordances
    // (e.g. "+ New") would be ambiguous as a bare icon next to the section's
    // first row. Sections rely on `AppSidebarNav`'s `space-y-3` between siblings
    // for vertical separation; we don't add extra `pt-3` in collapsed mode or
    // sections would double-pad and the rail would feel sparse.
    const showHeader = !collapsed && label != null

    // An untitled section at the top of the nav (Home and its quick links)
    // gets 8px on top of the nav's own 8px, so its first row clears the
    // divider above by more than the row's side inset. Only as the nav's
    // first child (in the mobile drawer the app list leads and keeps its gap),
    // and only in the unified shell.
    return (
      <div ref={ref} className={cn(label != null ? 'pt-3' : unified && 'first:pt-2', className)} {...props}>
        {showHeader && (
          <div className="flex items-center justify-between px-2 mb-1">
            {labelAsChild ? (
              <Slot className={cn(SIDEBAR_SECTION_LABEL, 'hover:text-foreground transition-colors')}>
                {label as React.ReactElement}
              </Slot>
            ) : (
              <span className={SIDEBAR_SECTION_LABEL}>{label}</span>
            )}
            {action}
          </div>
        )}
        <ul className="space-y-0.5">{visible}</ul>
        {shouldTruncate && !collapsed && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-0.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            {expanded ? lessLabel : (moreLabel ?? ((n) => `Show ${n} more`))(hidden)}
          </button>
        )}
      </div>
    )
  },
)
AppSidebarSection.displayName = 'AppSidebarSection'

export interface AppSidebarIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

/**
 * Canonical 20×20 sidebar icon-button shape — used by section action slots
 * (`AppSidebarSectionAddButton`) and row trailing actions alike. Sharing
 * this primitive keeps every "+" / "⋮" / similar icon button vertically
 * aligned: same outer box (h-5 w-5), same hover surface, same hit area.
 * Apps building dropdown triggers should pass `asChild` and an icon child
 * sized `h-3.5 w-3.5` so the icon center lands on the same baseline.
 */
const AppSidebarIconButton = React.forwardRef<HTMLButtonElement, AppSidebarIconButtonProps>(
  ({ asChild, className, children, ...props }, ref) => {
    const classes = cn(
      'inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-sidebar-accent hover:text-foreground transition-colors',
      className,
    )
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          {children as React.ReactElement}
        </Slot>
      )
    }
    return (
      <button ref={ref} type="button" className={classes} {...props}>
        {children}
      </button>
    )
  },
)
AppSidebarIconButton.displayName = 'AppSidebarIconButton'

export interface AppSidebarSectionAddButtonProps extends AppSidebarIconButtonProps {}

/** Small "+" icon button for use in `AppSidebarSection`'s `action` slot. */
const AppSidebarSectionAddButton = React.forwardRef<HTMLButtonElement, AppSidebarSectionAddButtonProps>(
  ({ children, ...props }, ref) => (
    <AppSidebarIconButton ref={ref} aria-label="Add" {...props}>
      {children ?? <Plus className="h-3.5 w-3.5" />}
    </AppSidebarIconButton>
  ),
)
AppSidebarSectionAddButton.displayName = 'AppSidebarSectionAddButton'

// ------------------------------------------------------------------
// Item
// ------------------------------------------------------------------

export interface AppSidebarItemProps extends React.HTMLAttributes<HTMLElement> {
  /** Plain leading icon (Lucide svg, etc.). */
  icon?: React.ReactNode
  /** Colored rounded-tile leading icon. Overrides `icon` when set. */
  tile?: {
    icon: React.ReactNode
    colorKey?: string
    /** Arbitrary hex color override. Only used with `variant: 'solid'`. */
    color?: string
    /** Hex bg color (multipass `App.bgColor`). With `fgColor`, renders the
     *  25%-rounded brand tile and overrides colorKey/color. */
    bgColor?: string | null
    /** Hex icon color (multipass `App.fgColor`). */
    fgColor?: string | null
    size?: 'sm' | 'md' | 'lg'
    variant?: 'soft' | 'solid'
  }
  /** Optional trailing content (e.g. `>` chevron on expandable items). */
  trailing?: React.ReactNode
  active?: boolean
  asChild?: boolean
  href?: string
  /** Render the row greyed-out and non-interactive (no navigation, no
   *  click). Used to gate nav items behind a precondition (e.g. a school
   *  admin must finish onboarding before Orders/Settings unlock). */
  disabled?: boolean
  /** Indent the row under the row above it (Tasks' boards under a team). */
  inset?: boolean
}

// Single source of truth for sidebar row text size. Mobile gets 16px (touch
// readability + matches iOS conventions); desktop tightens to 14px. Every
// sidebar row — items, app-switcher dropdown, app-switcher brand header —
// references this so they can never drift apart.
const SIDEBAR_ROW_TEXT = 'text-base md:text-sm'

const appSidebarItemClasses = (active: boolean | undefined, unified: boolean) =>
  cn(
    // iOS Safari leaves :focus-visible stuck on buttons after a tap, so we
    // suppress the default outline on every sidebar element. Keyboard accessibility
    // is preserved via Tab order + aria-current/expanded — the visible focus ring
    // adds no value here and creates a "why is this highlighted?" UX bug.
    // px derived from SWITCHER_ITEM_PX — participates in the switcher
    // icon-column arithmetic (see computeRowTileLeft).
    TAILWIND_PX[SWITCHER_ITEM_PX],
    'flex w-full items-center gap-2 py-3 md:py-1.5 transition-colors focus:outline-none focus-visible:outline-none',
    sidebarRadius(unified),
    SIDEBAR_ROW_TEXT,
    active
      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
      : 'text-sidebar-foreground hover:bg-sidebar-accent',
  )

const renderLeading = (
  tile: AppSidebarItemProps['tile'] | undefined,
  icon: React.ReactNode,
  unified: boolean,
): React.ReactNode => {
  if (tile) {
    return (
      <AppSidebarTile
        icon={tile.icon}
        colorKey={tile.colorKey}
        color={tile.color}
        bgColor={tile.bgColor}
        fgColor={tile.fgColor}
        size={tile.size ?? 'sm'}
        variant={tile.variant ?? 'soft'}
      />
    )
  }
  // A classic sidebar sizes its own icons, as it always has.
  if (!unified) {
    return icon
  }
  if (icon == null || typeof icon === 'boolean') {
    return null
  }
  return <span className={SIDEBAR_ICON_SLOT}>{icon}</span>
}

// Icon-only row geometry. A 36px square hit target gives every row the same
// visible footprint regardless of whether its leading slot is a 16px lucide
// stroke or a 28px colored tile, so the active/hover background fills a
// consistent shape down the rail.
const collapsedItemClasses = (active: boolean | undefined, unified: boolean) =>
  cn(
    'flex h-9 w-9 items-center justify-center transition-colors focus:outline-none focus-visible:outline-none',
    sidebarRadius(unified),
    active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-sidebar-foreground hover:bg-sidebar-accent',
  )

const AppSidebarItem = React.forwardRef<HTMLElement, AppSidebarItemProps>(
  ({ icon, tile, trailing, active, asChild, href, disabled, inset, className, children, onClick, ...props }, ref) => {
    const { onNavigate } = React.useContext(AppSidebarContext)
    const collapsed = useAppSidebarCollapsed()
    const unified = useUnifiedLook()
    const handleClick: React.MouseEventHandler<HTMLElement> = (event) => {
      onClick?.(event as React.MouseEvent<HTMLElement>)
      if (!event.defaultPrevented) {
        onNavigate?.()
      }
    }
    const leading = renderLeading(tile, icon, unified)
    // Two row shapes: a full-width row in expanded mode, a 36px square in
    // collapsed mode. Centering the square inside the rail keeps icons of
    // varying widths (lucide strokes vs colored tiles) optically aligned
    // around the rail's vertical axis. Brief x-shift during the 200ms
    // collapse/expand transition is acceptable — the steady states are what
    // the eye reads.
    const classes = cn(
      collapsed ? collapsedItemClasses(active, unified) : appSidebarItemClasses(active, unified),
      inset && !collapsed && SIDEBAR_INSET_PL,
      disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
      className,
    )

    // Resolve the label content used by the collapsed-mode tooltip. For
    // `asChild` rows the label lives inside the wrapped element (e.g. a
    // `<Link>...</Link>`); otherwise it's the row's own children.
    const tooltipLabel: React.ReactNode =
      asChild && React.isValidElement(children) ? (children.props as { children?: React.ReactNode }).children : children

    let interactive: React.ReactElement
    if (disabled) {
      // Greyed-out, non-navigable row. We drop the wrapped <Link> / href /
      // button entirely so neither click nor keyboard activates it, and mark
      // it aria-disabled for assistive tech.
      const inner = collapsed ? (
        leading
      ) : (
        <>
          {leading}
          <span className="flex-1 min-w-0 truncate text-left">{tooltipLabel}</span>
          {trailing}
        </>
      )
      interactive = (
        <div
          ref={ref as unknown as React.Ref<HTMLDivElement>}
          {...(props as React.HTMLAttributes<HTMLDivElement>)}
          aria-disabled="true"
          className={classes}
          {...(collapsed && typeof tooltipLabel === 'string' ? { 'aria-label': tooltipLabel } : null)}>
          {inner}
        </div>
      )
    } else if (asChild) {
      const child = React.Children.only(children) as React.ReactElement<{
        className?: string
        children?: React.ReactNode
      }>
      // Child's className is treated as the default; AppSidebarItem's classes
      // win on tailwind-merge conflicts. This matters for layout utilities like
      // `flex` — older call sites pass `className="block"` on the inner Link,
      // which would otherwise override our flex layout and silently break gap.
      const mergedProps = {
        className: cn(child.props.className, classes),
        onClick: handleClick,
        ...props,
      }
      const inner = collapsed ? (
        leading
      ) : (
        <>
          {leading}
          <span className="flex-1 min-w-0 truncate text-left">{child.props.children}</span>
          {trailing}
        </>
      )
      interactive = (
        <Slot ref={ref} {...mergedProps}>
          {React.cloneElement(child, {}, inner)}
        </Slot>
      )
    } else {
      const inner = collapsed ? (
        leading
      ) : (
        <>
          {leading}
          <span className="flex-1 min-w-0 truncate text-left">{children}</span>
          {trailing}
        </>
      )
      const sharedProps = {
        className: classes,
        onClick: handleClick,
        // Surface a string label as `aria-label` when collapsed so screen
        // readers still announce the row's purpose without the visible text.
        ...(collapsed && typeof tooltipLabel === 'string' ? { 'aria-label': tooltipLabel } : null),
      }
      if (href) {
        interactive = (
          <a
            ref={ref as unknown as React.Ref<HTMLAnchorElement>}
            href={href}
            {...sharedProps}
            {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
            {inner}
          </a>
        )
      } else {
        interactive = (
          <button
            ref={ref as unknown as React.Ref<HTMLButtonElement>}
            type="button"
            {...sharedProps}
            {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
            {inner}
          </button>
        )
      }
    }

    if (collapsed) {
      return (
        <li className="flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>{interactive}</TooltipTrigger>
            <TooltipContent side="right" sideOffset={12}>
              {tooltipLabel}
            </TooltipContent>
          </Tooltip>
        </li>
      )
    }
    return <li>{interactive}</li>
  },
)
AppSidebarItem.displayName = 'AppSidebarItem'

export interface AppSidebarSeparatorProps extends React.HTMLAttributes<HTMLLIElement> {
  /** Line up with `inset` rows (Tasks' boards under a team). */
  inset?: boolean
}

/** Hairline between two runs of rows inside one section (Tasks' manual
 *  boards above its app boards). */
const AppSidebarSeparator = React.forwardRef<HTMLLIElement, AppSidebarSeparatorProps>(
  ({ inset, className, ...props }, ref) => {
    const collapsed = useAppSidebarCollapsed()
    return (
      <li
        ref={ref}
        role="separator"
        className={cn('my-1 mr-2 border-t border-border/60', inset && !collapsed ? 'ml-7' : 'ml-2', className)}
        {...props}
      />
    )
  },
)
AppSidebarSeparator.displayName = 'AppSidebarSeparator'

// ------------------------------------------------------------------
// Collapsible group (unchanged)
// ------------------------------------------------------------------

export interface AppSidebarCollapsibleGroupProps {
  icon?: React.ReactNode
  label: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  level?: 0 | 1
  className?: string
  triggerClassName?: string
  /** When false, render the group always-open with no chevron + no toggle. Default true. */
  collapsible?: boolean
  children: React.ReactNode
}

const AppSidebarCollapsibleGroup = React.forwardRef<HTMLDivElement, AppSidebarCollapsibleGroupProps>(
  (
    {
      icon,
      label,
      open,
      defaultOpen,
      onOpenChange,
      level = 0,
      className,
      triggerClassName,
      collapsible = true,
      children,
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false)
    const unified = useUnifiedLook()
    const isControlled = open !== undefined
    const isOpen = isControlled ? open : internalOpen
    const handleOpenChange = (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next)
      }
      onOpenChange?.(next)
    }

    if (!collapsible) {
      return (
        <li ref={ref as React.Ref<HTMLLIElement>} className={cn(className)}>
          <div
            className={cn(
              'flex w-full items-center px-2 py-3 md:py-1.5 text-sidebar-foreground',
              sidebarRadius(unified),
              SIDEBAR_ROW_TEXT,
              triggerClassName,
            )}>
            <span className="flex items-center gap-2">
              {renderLeading(undefined, icon, unified)}
              {label}
            </span>
          </div>
          <ul className={cn('mt-0.5 space-y-0.5', level === 1 ? SIDEBAR_INSET_PL : 'pl-4')}>{children}</ul>
        </li>
      )
    }

    return (
      <CollapsiblePrimitive.Root ref={ref} open={isOpen} onOpenChange={handleOpenChange} className={cn(className)}>
        <li>
          <CollapsiblePrimitive.Trigger
            className={cn(
              'flex w-full items-center justify-between px-2 py-3 md:py-1.5 text-sidebar-foreground hover:bg-sidebar-accent transition-colors focus:outline-none focus-visible:outline-none',
              sidebarRadius(unified),
              SIDEBAR_ROW_TEXT,
              triggerClassName,
            )}>
            <span className="flex items-center gap-2">
              {renderLeading(undefined, icon, unified)}
              {label}
            </span>
            {isOpen ? (
              <ChevronDown className="h-4 w-4 md:h-3.5 md:w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 md:h-3.5 md:w-3.5 text-muted-foreground" />
            )}
          </CollapsiblePrimitive.Trigger>
          <CollapsiblePrimitive.Content>
            <ul className={cn('mt-0.5 space-y-0.5', level === 1 ? SIDEBAR_INSET_PL : 'pl-4')}>{children}</ul>
          </CollapsiblePrimitive.Content>
        </li>
      </CollapsiblePrimitive.Root>
    )
  },
)
AppSidebarCollapsibleGroup.displayName = 'AppSidebarCollapsibleGroup'

const AppSidebarFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('border-t border-sidebar-border px-3 py-2 shrink-0', className)} {...props}>
      {children}
    </div>
  ),
)
AppSidebarFooter.displayName = 'AppSidebarFooter'

// ------------------------------------------------------------------
// User tile (leaf) + compound user menu (popover)
// ------------------------------------------------------------------

/**
 * Duck-typed user shape the sidebar tile + menu accept via the `user`
 * prop. Different Ordo apps wire different identity sources (Multipass
 * `PublicUser.picture`, local DB user `image`, third-party `imageUrl`),
 * so the primitive accepts all three rather than forcing every app to
 * remember which one its source uses. Pass `user={user}` and the tile
 * picks the first non-null avatar field.
 */
export interface AppSidebarUserLike {
  name?: string | null
  email?: string | null
  picture?: string | null
  image?: string | null
  imageUrl?: string | null
}

function pickAvatarSrc(user: AppSidebarUserLike | null | undefined): string | null {
  if (!user) {
    return null
  }
  return user.picture ?? user.image ?? user.imageUrl ?? null
}

export interface AppSidebarUserTileProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Preferred. Pass the app's auth user / Multipass `PublicUser`
   * directly and let the primitive resolve `name` / `email` / avatar
   * from it (handles `picture` / `image` / `imageUrl` field-name
   * variation). Explicit `name` / `email` / `imageSrc` props still win
   * when set.
   */
  user?: AppSidebarUserLike | null
  name?: string | null
  email?: string | null
  imageSrc?: string | null
  initials?: string
  asChild?: boolean
}

const computeInitials = (name?: string | null, email?: string | null) => {
  if (name) {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

/**
 * Resolve the identity a sidebar user surface should display. Explicit
 * `name` / `email` / `imageSrc` / `initials` win over whatever the `user`
 * object carries — callers are split between the two prop styles.
 *
 * Coalesces on FALSY, not nullish: tasker passes `user.name ?? ''` /
 * `user.image ?? ''`, and an empty string has to fall through to the next
 * source instead of rendering as a blank line.
 *
 * Shared by `AppSidebarUserTile` (the trigger) and `AppSidebarUserMenu`'s
 * identity header so the two can never disagree about who is signed in.
 */
export const resolveSidebarUser = ({
  user,
  name,
  email,
  imageSrc,
  initials,
}: {
  user?: AppSidebarUserLike | null
  name?: string | null
  email?: string | null
  imageSrc?: string | null
  initials?: string
}) => {
  const resolvedName = name || user?.name || null
  const resolvedEmail = email || user?.email || null
  return {
    name: resolvedName,
    email: resolvedEmail,
    imageSrc: imageSrc || pickAvatarSrc(user) || null,
    initials: initials || computeInitials(resolvedName, resolvedEmail),
    /** Single-line display name: the email stands in when there is no name. */
    label: resolvedName ?? resolvedEmail ?? 'User',
  }
}

const AppSidebarUserTile = React.forwardRef<HTMLElement, AppSidebarUserTileProps>(
  ({ user, name, email, imageSrc, initials, asChild, className, children, ...props }, ref) => {
    const {
      imageSrc: resolvedImage,
      initials: fallbackInitials,
      label,
    } = resolveSidebarUser({ user, name, email, imageSrc, initials })
    const collapsed = useAppSidebarCollapsed()
    const unified = useUnifiedLook()
    // Centered avatar-only tile in collapsed mode, matching the icon-row
    // geometry above so the rail reads as one column. The popover still
    // opens on click; the tooltip wired by `AppSidebarUserMenu` carries
    // the user's name on hover.
    const classes = cn(
      collapsed
        ? cn(
            'flex w-full items-center justify-center py-1.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors',
            sidebarRadius(unified),
          )
        : cn(
            'flex w-full items-center gap-2 px-2 py-1.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors',
            sidebarRadius(unified),
          ),
      className,
    )
    const content =
      children ??
      (collapsed ? (
        <Avatar className="h-8 w-8 shrink-0">
          {resolvedImage ? <AvatarImage src={resolvedImage} alt={label} /> : null}
          <AvatarFallback>{fallbackInitials}</AvatarFallback>
        </Avatar>
      ) : (
        <>
          <Avatar className="h-8 w-8 shrink-0">
            {resolvedImage ? <AvatarImage src={resolvedImage} alt={label} /> : null}
            <AvatarFallback>{fallbackInitials}</AvatarFallback>
          </Avatar>
          <span className="flex-1 truncate text-left font-medium">{label}</span>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
        </>
      ))

    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          <button type="button">{content}</button>
        </Slot>
      )
    }

    return (
      <button
        ref={ref as unknown as React.Ref<HTMLButtonElement>}
        type="button"
        className={classes}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
        {content}
      </button>
    )
  },
)
AppSidebarUserTile.displayName = 'AppSidebarUserTile'

/** Light / Dark / System, in the order the segmented control renders them.
 *  Ids match multipass's `color_mode` allowed values exactly, so the
 *  chosen id is the value that gets persisted. */
const COLOR_MODE_VIEWS: ViewOption[] = [
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'dark', label: 'Dark', icon: Moon },
  { id: 'system', label: 'System', icon: Monitor },
]

export interface AppSidebarUserMenuProps {
  /**
   * Preferred. Pass the app's auth user / Multipass `PublicUser`
   * directly and let the primitive resolve `name` / `email` / avatar
   * from it. See `AppSidebarUserLike`.
   */
  user?: AppSidebarUserLike | null
  name?: string | null
  email?: string | null
  imageSrc?: string | null
  initials?: string
  /**
   * Controlled dark-mode state. Apps own their theme store.
   *
   * @deprecated Binary on/off cannot express "follow the OS". Pass
   * `colorMode` / `onColorModeChange` instead (PROD-986) — typically
   * straight from `useColorMode()`. Still honored so no consumer breaks,
   * and used as a fallback when the three-way props are absent.
   */
  darkMode?: boolean
  /** @deprecated See {@link AppSidebarUserMenuProps.darkMode}. */
  onDarkModeChange?: (dark: boolean) => void
  /** Controlled three-way color mode. Takes precedence over `darkMode`. */
  colorMode?: ColorMode
  onColorModeChange?: (mode: ColorMode) => void
  onProfile?: () => void
  /**
   * Opens the person's notification settings. Renders a "Notifications" row
   * with a bell icon between Theme and Logout; omitted, there is no row.
   */
  onNotifications?: () => void
  onSignOut?: () => void
  /** Override the profile row label/icon. */
  profileLabel?: React.ReactNode
  /** Override the sign-out row label/icon. */
  signOutLabel?: React.ReactNode
  /** Override the notifications row label. */
  notificationsLabel?: React.ReactNode
  /** Render additional menu rows between the built-in ones and Logout. */
  extraItems?: React.ReactNode
}

function AppSidebarUserMenuRow({
  icon,
  children,
  onClick,
  trailing,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  onClick?: () => void
  trailing?: React.ReactNode
}) {
  const classes =
    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors'
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(classes, 'text-left')}>
        <span className="flex h-5 w-5 items-center justify-center text-muted-foreground">{icon}</span>
        <span className="flex-1 truncate">{children}</span>
        {trailing}
      </button>
    )
  }
  return (
    <div className={classes}>
      <span className="flex h-5 w-5 items-center justify-center text-muted-foreground">{icon}</span>
      <span className="flex-1 truncate">{children}</span>
      {trailing}
    </div>
  )
}

function AppSidebarUserMenu({
  user,
  name,
  email,
  imageSrc,
  initials,
  darkMode,
  onDarkModeChange,
  colorMode,
  onColorModeChange,
  onProfile,
  onNotifications,
  onSignOut,
  profileLabel,
  signOutLabel,
  notificationsLabel,
  extraItems,
}: AppSidebarUserMenuProps) {
  // Controlled open state so menu rows that perform an action (Profile,
  // Sign Out, etc.) auto-close the popover. Without this the popover
  // stays open after navigation since Radix only auto-dismisses on
  // outside-click / Escape — not on inner button clicks.
  const [open, setOpen] = React.useState(false)
  const collapsed = useAppSidebarCollapsed()
  const closeAfter = (handler?: () => void) => {
    if (!handler) {
      return undefined
    }
    return () => {
      setOpen(false)
      handler()
    }
  }
  const identity = resolveSidebarUser({ user, name, email, imageSrc, initials })
  const tile = <AppSidebarUserTile user={user} name={name} email={email} imageSrc={imageSrc} initials={initials} />
  // Skip the tooltip while the popover is open — Radix tooltip + popover
  // sharing the same trigger flickers if both try to react to the same
  // hover/focus state. The popover is the user's intent in that moment.
  const trigger =
    collapsed && !open ? (
      <Tooltip>
        <TooltipTrigger asChild>{tile}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={12}>
          {identity.label}
        </TooltipContent>
      </Tooltip>
    ) : (
      tile
    )
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={8} className="w-[240px] p-1">
        {identity.name || identity.email ? (
          <div className="mb-1 flex items-center gap-2 border-b border-border px-2 py-1.5">
            <Avatar className="h-8 w-8 shrink-0">
              {identity.imageSrc ? <AvatarImage src={identity.imageSrc} alt={identity.label} /> : null}
              <AvatarFallback>{identity.initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">{identity.label}</div>
              {/* Skipped when the name IS the email — hq/previews/rfp derive
                  their display name from it, so both lines would read alike. */}
              {identity.email && identity.email !== identity.label ? (
                <div className="truncate text-xs text-muted-foreground">{identity.email}</div>
              ) : null}
            </div>
          </div>
        ) : null}
        {onColorModeChange ? (
          <AppSidebarUserMenuRow
            icon={<Monitor className="h-4 w-4" />}
            trailing={
              <ViewToggle
                iconOnly
                views={COLOR_MODE_VIEWS}
                // `darkMode` is the fallback for a caller still on the
                // binary props: it can only ever mean light or dark, so
                // "System" simply reads as unselected there.
                activeView={colorMode ?? (darkMode ? 'dark' : 'light')}
                onChange={(next) => {
                  if (isColorMode(next)) {
                    onColorModeChange(next)
                  }
                }}
              />
            }>
            Theme
          </AppSidebarUserMenuRow>
        ) : (
          onDarkModeChange && (
            <AppSidebarUserMenuRow
              icon={<Moon className="h-4 w-4" />}
              trailing={
                <Switch
                  checked={!!darkMode}
                  onCheckedChange={(next) => onDarkModeChange(next)}
                  aria-label="Dark mode"
                />
              }>
              Dark mode
            </AppSidebarUserMenuRow>
          )
        )}
        {onNotifications && (
          <AppSidebarUserMenuRow icon={<Bell className="h-4 w-4" />} onClick={closeAfter(onNotifications)}>
            {notificationsLabel ?? 'Notifications'}
          </AppSidebarUserMenuRow>
        )}
        {onProfile && (
          <AppSidebarUserMenuRow icon={<UserIcon className="h-4 w-4" />} onClick={closeAfter(onProfile)}>
            {profileLabel ?? 'Profile'}
          </AppSidebarUserMenuRow>
        )}
        {extraItems}
        {onSignOut && (
          <AppSidebarUserMenuRow icon={<LogOut className="h-4 w-4" />} onClick={closeAfter(onSignOut)}>
            {signOutLabel ?? 'Logout'}
          </AppSidebarUserMenuRow>
        )}
      </PopoverContent>
    </Popover>
  )
}

// ------------------------------------------------------------------
// App switcher (popover) — replaces AppSidebarBrand in the universal layout
// ------------------------------------------------------------------

export interface AppSwitcherApp {
  id: string
  name: string
  href: string
  icon: React.ReactNode
  /** Admin-managed self-contained tile SVG (48×48 with bg + icon baked in).
   *  When set, the dropdown row renders this directly instead of wrapping
   *  `icon` in a colored tile — so the sidebar matches HQ's "My Apps" grid. */
  image?: string | null
  /** A complete brand tile (e.g. Ordo's "o" app icon) that wins over
   *  `image` and the registry icon. */
  logo?: React.ReactNode
  /** Hex tile background color (multipass `App.bgColor`). Paired with
   *  `fgColor`, drives the brand-tile look in the launcher. */
  bgColor?: string | null
  /** Hex tile icon color (multipass `App.fgColor`). */
  fgColor?: string | null
  /** Available to consumers (e.g. HQ's "My Apps" tile grid). The sidebar dropdown does not render it. */
  description?: string | null
}

export interface AppSidebarAppSwitcherProps {
  /** Icon for the currently selected app (rendered inside the colored tile). */
  appIcon: React.ReactNode
  /** Name of the currently selected app. */
  appName: string
  /** Color key for the current app's tile. */
  appColorKey?: string
  /** Optional override of the apps list. When omitted, fetched from HQ via `useOrdoApps`. */
  apps?: AppSwitcherApp[]
  /** ID of the current app. Used by `AppSidebarAppsList`'s `hideCurrent` filter. */
  currentAppId?: string
  /** When true (default), the brand row gets a chevron `Trigger` that toggles the
   *  apps list (`AppSidebarAppsList` rendered elsewhere in the body). When false,
   *  no chevron and the apps list stays open — same single-column layout either way. */
  collapsible?: boolean
  /** When true (default), drop the current app's row from the list. */
  hideCurrent?: boolean
  /** Render prop to wrap the brand row's tile + name so a click navigates home —
   *  typically the app's home route. When provided, the brand area gains a hover
   *  state matching the rest of the sidebar. The chevron (when shown) is a
   *  separate click target and is unaffected. */
  renderHomeLink?: (children: React.ReactNode) => React.ReactNode
  className?: string
}

function AppSidebarAppSwitcher({
  appIcon,
  appName,
  appColorKey = 'gray',
  apps: appsProp,
  currentAppId,
  collapsible = true,
  hideCurrent = true,
  renderHomeLink,
  className,
}: AppSidebarAppSwitcherProps) {
  // Caller can override (mostly for tests / storybook); default fetches from HQ.
  const { apps: fetchedApps, isLoading: appsLoading } = useOrdoApps()
  const apps = appsProp ?? fetchedApps
  const visibleApps = hideCurrent && currentAppId ? apps.filter((a) => a.id !== currentAppId) : apps
  const collapsed = useAppSidebarCollapsed()
  const { appBar } = React.useContext(AppSidebarContext)

  // Show the chevron when the host opted into collapsible mode AND there's
  // something to switch to. While the registry is still fetching, treat
  // "has apps" as optimistically true so the chevron doesn't pop in when
  // the response lands — the steady state for every prod org is that the
  // registry returns a non-empty list. If the fetch resolves with zero
  // visible apps (e.g. unconfigured tenant), the chevron disappears and
  // the brand row falls back to the home-link path.
  // The chevron also disappears in collapsed mode — there's no room for it
  // and the apps list is rendered as icon-only tiles via `AppSidebarItem`.
  const isRegistryLoading = appsProp === undefined && appsLoading
  const hasAppsToShow = visibleApps.length > 0 || isRegistryLoading
  // While the horizontal app bar (PROD-1403) is showing every app is one click
  // away, so the header becomes a plain brand row.
  const showChevron = collapsible && hasAppsToShow && !collapsed && !appBar
  // The trigger's tile is resolved from the SAME registry entry the app
  // rows use (current app by id), through the SAME renderer — full-bleed
  // admin tile SVG when available, brand/fallback AppSidebarTile otherwise —
  // so the trigger can't render a smaller or differently-styled box than
  // the rows below it. The host-passed `appIcon`/`appColorKey` remain the
  // fallback for registries that don't know the current app.
  const currentApp = currentAppId ? apps.find((a) => a.id === currentAppId) : undefined
  const triggerTile = renderSwitcherTile(currentApp, { icon: appIcon, colorKey: appColorKey })
  // In collapsed mode the tile centers in the 56px rail so it shares an
  // optical axis with the centered icon rows below. In expanded mode the
  // shared trigger inset applies (one icon column with the app rows — see
  // SWITCHER_TRIGGER_INSET_CLASS) and the app name follows.
  const brandInner = collapsed ? (
    <span className="flex h-full w-full items-center justify-center text-sidebar-foreground">{triggerTile}</span>
  ) : (
    <span
      className={cn(
        'flex h-full min-w-0 flex-1 items-center gap-2 text-sidebar-foreground',
        SWITCHER_TRIGGER_INSET_CLASS,
      )}>
      {triggerTile}
      <span className={cn('flex-1 truncate text-left font-semibold', SIDEBAR_ROW_TEXT)}>{appName}</span>
    </span>
  )

  // When there are apps to switch to, the entire top-left block toggles the
  // apps list — one click target, one hover state, no internal divider.
  // `renderHomeLink` is intentionally ignored in this mode; apps that need a
  // home affordance render a dedicated "Home" item in the body nav. When
  // there's nothing to switch to (HQ, single-app world), fall back to a
  // plain row that the host can wrap with a home link.
  const { scrollBodyToTop } = React.useContext(AppSidebarContext)
  if (showChevron) {
    return (
      <CollapsiblePrimitive.Trigger
        aria-label="Toggle apps list"
        onClick={scrollBodyToTop}
        className={cn(
          // The trigger row ALWAYS keeps its bottom hairline — it boxes the
          // current app (per Hunter's PR #1427 review). While the panel is
          // open a second hairline closes the apps list below the last app.
          'group flex w-full shrink-0 items-center border-b border-sidebar-border bg-sidebar text-left hover:bg-sidebar-accent transition-colors focus:outline-none focus-visible:outline-none',
          APP_CHROME_HEIGHT_CLASS,
          className,
        )}>
        {brandInner}
        <span className="flex h-full w-12 items-center justify-center">
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
        </span>
      </CollapsiblePrimitive.Trigger>
    )
  }

  // In collapsed mode the brand row is just the home-link tile — wrap with
  // a tooltip so the app name still reaches the user on hover, matching the
  // behaviour of every other icon-only row in the rail.
  const brandRow = renderHomeLink ? (
    <span className="flex h-full min-w-0 flex-1 hover:bg-sidebar-accent transition-colors [&>*]:!flex [&>*]:!h-full [&>*]:min-w-0 [&>*]:flex-1 [&>*]:items-center">
      {renderHomeLink(brandInner)}
    </span>
  ) : (
    brandInner
  )
  const brandWrapped = collapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block h-full">{brandRow}</span>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={12}>
        {appName}
      </TooltipContent>
    </Tooltip>
  ) : (
    brandRow
  )
  // The bottom hairline stays even though the list below is always open in
  // this non-collapsible branch — it boxes the current app's row (per
  // Hunter's PR #1427 review); the list closes with its own line below the
  // last app.
  return (
    <div className={cn('flex shrink-0 border-b border-sidebar-border bg-sidebar', APP_CHROME_HEIGHT_CLASS, className)}>
      <span className="flex h-full min-w-0 flex-1">{brandWrapped}</span>
    </div>
  )
}
// `displayName` is what `AppSidebar` looks for to peel this child out into the
// pinned-header slot. Function components don't carry one by default.
AppSidebarAppSwitcher.displayName = 'AppSidebarAppSwitcher'

// ------------------------------------------------------------------
// Apps list (flat) — the body-side companion to a non-collapsible
// AppSidebarAppSwitcher. Fetches the same Ordo apps registry and renders
// each one as an AppSidebarItem so the rows match the rest of the sidebar.
// ------------------------------------------------------------------

export interface AppSidebarAppsListProps {
  /** Optional override of the apps list. When omitted, fetched from HQ via `useOrdoApps`. */
  apps?: AppSwitcherApp[]
  /** ID of the current app. When `hideCurrent` is true (default) its row is suppressed. */
  currentAppId?: string
  /** When true (default), drop the current app's row. Set to false to render every app. */
  hideCurrent?: boolean
  /** Render prop for the app link (e.g. React Router `<Link>`). Default: plain `<a>`. */
  renderAppLink?: (app: AppSwitcherApp, children: React.ReactNode) => React.ReactNode
  className?: string
}

function AppSidebarAppsList({
  apps: appsProp,
  currentAppId,
  hideCurrent = true,
  renderAppLink,
  className,
}: AppSidebarAppsListProps) {
  const { apps: fetchedApps } = useOrdoApps()
  const collapsed = useAppSidebarCollapsed()
  const { appBar } = React.useContext(AppSidebarContext)
  const apps = appsProp ?? fetchedApps
  const visibleApps = hideCurrent && currentAppId ? apps.filter((a) => a.id !== currentAppId) : apps
  // While the horizontal app bar (PROD-1403) shows every app, the body list
  // would only duplicate it. The mobile drawer has no rail and keeps it.
  if (appBar) {
    return null
  }
  // Wrap in `Collapsible.Content` so the chevron `Trigger` rendered inside
  // `AppSidebarAppSwitcher`'s brand row can animate this list open/closed.
  // The shared `Collapsible.Root` lives on `AppSidebar`. When the host
  // doesn't render a chevron (HQ uses `collapsible={false}`), the Root's
  // open state stays at its default `true` and this Content is always
  // visible — same single-column layout, no animation surprise.
  //
  // No `forceMount`: Radix unmounts the content while closed so the rows
  // genuinely disappear. `forceMount` was previously set with the comment
  // "so the height animation has something to measure," but that's a
  // misread — Radix computes `--radix-collapsible-content-height` from
  // the mounted content during the open transition and unmounts cleanly
  // when closed. Leaving it on with no `data-[state=closed]:hidden`
  // sibling class meant the closed state showed full content (animation
  // ran but didn't stick once it ended).
  return (
    <CollapsiblePrimitive.Content className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
      {/* Expanded mode: the apps group is boxed by the switcher trigger's
          own hairline above (it boxes the CURRENT app row) and this BOTTOM
          divider below the last app (per PROD ticket
          cmokyo7jl001y012istt0bpt5: "the separation line should be at the
          bottom … below website"). Icon column: hosts render this list
          inside AppSidebarNav (SIDEBAR_NAV_PX) and each AppSidebarItem
          pads SWITCHER_ITEM_PX, so this <ul> contributes SWITCHER_LIST_PX
          = 0 — nav 8 + ul 0 + item 8 = 16px, exactly the trigger's
          SWITCHER_TRIGGER_PX (see computeRowTileLeft/computeTriggerTileLeft;
          one shared tile renderer, one box size — renderSwitcherTile).
          Collapsed (icon-rail) mode skips the inset so tiles stay centered
          in the rail. */}
      <ul
        className={cn(
          'space-y-0.5',
          collapsed ? undefined : cn('border-b border-sidebar-border pb-1.5', SWITCHER_LIST_INSET_CLASS),
          className,
        )}>
        {visibleApps.map((app) => {
          // Same renderer as the trigger tile (see renderSwitcherTile):
          // admin-managed self-contained tile SVGs render full-bleed;
          // otherwise the brand/gray AppSidebarTile — always the same box.
          return (
            <AppSidebarItem
              key={app.id}
              asChild
              active={app.id === currentAppId}
              icon={renderSwitcherTile(app, { icon: app.icon })}>
              {renderAppLink ? renderAppLink(app, app.name) : <a href={app.href}>{app.name}</a>}
            </AppSidebarItem>
          )
        })}
      </ul>
    </CollapsiblePrimitive.Content>
  )
}

// ------------------------------------------------------------------
// Named section variants (design-system canon)
//
// These are the approved group shapes. Apps should use these instead
// of composing AppSidebarSection/CollapsibleGroup by hand. Each variant
// locks the visual contract so sidebars stay consistent across apps.
// ------------------------------------------------------------------

// --- Regular group: uppercase label + regular items -----------------

export interface AppSidebarRegularGroupProps {
  label: React.ReactNode
  className?: string
  children: React.ReactNode
}

function AppSidebarRegularGroup({ label, className, children }: AppSidebarRegularGroupProps) {
  return (
    <AppSidebarSection label={label} className={className}>
      {children}
    </AppSidebarSection>
  )
}

// --- Chats group: regular items + "Show N more" truncation ----------

export interface AppSidebarChatsGroupProps {
  label: React.ReactNode
  maxVisible: number
  /** Optional "+" action button for creating a new chat. */
  onAdd?: () => void
  addLabel?: string
  moreLabel?: (hidden: number) => string
  lessLabel?: string
  className?: string
  children: React.ReactNode
}

function AppSidebarChatsGroup({
  label,
  maxVisible,
  onAdd,
  addLabel,
  moreLabel,
  lessLabel,
  className,
  children,
}: AppSidebarChatsGroupProps) {
  return (
    <AppSidebarSection
      label={label}
      action={onAdd ? <AppSidebarSectionAddButton onClick={onAdd} aria-label={addLabel ?? 'New chat'} /> : undefined}
      maxVisible={maxVisible}
      moreLabel={moreLabel}
      lessLabel={lessLabel}
      className={className}>
      {children}
    </AppSidebarSection>
  )
}

// --- Color + icon group: solid colored tiles + white icon -----------

export interface AppSidebarColorIconGroupProps {
  label: React.ReactNode
  /** Optional "+" action button rendered next to the label. */
  onAdd?: () => void
  /** Optional "Show N more" truncation. */
  maxVisible?: number
  moreLabel?: (hidden: number) => string
  lessLabel?: string
  className?: string
  children: React.ReactNode
}

function AppSidebarColorIconGroup({
  label,
  onAdd,
  maxVisible,
  moreLabel,
  lessLabel,
  className,
  children,
}: AppSidebarColorIconGroupProps) {
  return (
    <AppSidebarSection
      label={label}
      action={onAdd ? <AppSidebarSectionAddButton onClick={onAdd} /> : undefined}
      maxVisible={maxVisible}
      moreLabel={moreLabel}
      lessLabel={lessLabel}
      className={className}>
      {children}
    </AppSidebarSection>
  )
}

export interface AppSidebarColorIconItemProps {
  /** Palette color key (e.g. "blue", "emerald"). */
  colorKey?: string
  /** Arbitrary hex color (overrides `colorKey`). For user-picked colors. */
  color?: string
  icon: React.ReactNode
  active?: boolean
  asChild?: boolean
  href?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  trailing?: React.ReactNode
  className?: string
  children: React.ReactNode
}

function AppSidebarColorIconItem({
  colorKey,
  color,
  icon,
  active,
  asChild,
  href,
  onClick,
  trailing,
  className,
  children,
}: AppSidebarColorIconItemProps) {
  return (
    <AppSidebarItem
      tile={{ icon, colorKey, color, size: 'sm', variant: 'solid' }}
      active={active}
      asChild={asChild}
      href={href}
      onClick={onClick}
      trailing={trailing}
      className={className}>
      {children}
    </AppSidebarItem>
  )
}

// --- Color group: collapsible (chevron-prefix label) + colored-dot items ---

export interface AppSidebarColorGroupProps {
  label: React.ReactNode
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  /** When false, render the group always-open with no chevron + no toggle. Default true. */
  collapsible?: boolean
  children: React.ReactNode
}

function AppSidebarColorGroup({
  label,
  defaultOpen = true,
  open,
  onOpenChange,
  className,
  collapsible = true,
  children,
}: AppSidebarColorGroupProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen
  const handleOpenChange = (next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next)
    }
    onOpenChange?.(next)
  }
  if (!collapsible) {
    return (
      <div className={cn('pt-3', className)}>
        <div className="flex items-center pl-2 pr-1 mb-1">
          <span className={SIDEBAR_SECTION_LABEL}>{label}</span>
        </div>
        <ul className="space-y-0.5">{children}</ul>
      </div>
    )
  }
  return (
    <CollapsiblePrimitive.Root open={isOpen} onOpenChange={handleOpenChange} className={cn('pt-3', className)}>
      <div className="flex items-center justify-between pl-2 pr-1 mb-1">
        <CollapsiblePrimitive.Trigger
          className={cn(
            'flex items-center gap-1 hover:text-foreground transition-colors focus:outline-none focus-visible:outline-none',
            SIDEBAR_SECTION_LABEL,
          )}>
          {isOpen ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
          <span>{label}</span>
        </CollapsiblePrimitive.Trigger>
      </div>
      <CollapsiblePrimitive.Content>
        <ul className="space-y-0.5">{children}</ul>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  )
}

export interface AppSidebarColorItemProps {
  /** Palette color key. */
  colorKey?: string
  /** Arbitrary hex color (overrides `colorKey`). For user-picked team colors. */
  color?: string
  active?: boolean
  asChild?: boolean
  href?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  className?: string
  children: React.ReactNode
}

function AppSidebarColorItem({
  colorKey,
  color,
  active,
  asChild,
  href,
  onClick,
  className,
  children,
}: AppSidebarColorItemProps) {
  const dot = color ? (
    <span aria-hidden className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: color }} />
  ) : (
    <TeamCircle colorKey={colorKey ?? 'slate'} />
  )
  return (
    <AppSidebarItem icon={dot} active={active} asChild={asChild} href={href} onClick={onClick} className={className}>
      {children}
    </AppSidebarItem>
  )
}

// --- Administration group: regular items + optional nested submenu ---

export interface AppSidebarAdminGroupProps {
  /** Defaults to "Administration" per the design spec. */
  label?: React.ReactNode
  className?: string
  children: React.ReactNode
}

function AppSidebarAdminGroup({ label = 'Administration', className, children }: AppSidebarAdminGroupProps) {
  return (
    <AppSidebarSection label={label} className={className}>
      {children}
    </AppSidebarSection>
  )
}

export interface AppSidebarAdminSubMenuProps {
  icon?: React.ReactNode
  label: React.ReactNode
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** When false, render the group always-open with no chevron + no toggle. Default true. */
  collapsible?: boolean
  children: React.ReactNode
}

function AppSidebarAdminSubMenu({
  icon,
  label,
  defaultOpen,
  open,
  onOpenChange,
  collapsible = true,
  children,
}: AppSidebarAdminSubMenuProps) {
  return (
    <AppSidebarCollapsibleGroup
      icon={icon}
      label={label}
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      collapsible={collapsible}>
      {children}
    </AppSidebarCollapsibleGroup>
  )
}

export {
  AppSidebar,
  AppSidebarAdminGroup,
  AppSidebarAdminSubMenu,
  AppSidebarAppsList,
  AppSidebarAppSwitcher,
  AppSidebarBrand,
  AppSidebarChatsGroup,
  AppSidebarCollapsibleGroup,
  AppSidebarColorGroup,
  AppSidebarColorIconGroup,
  AppSidebarColorIconItem,
  AppSidebarColorItem,
  AppSidebarDrawer,
  AppSidebarFooter,
  AppSidebarIconButton,
  AppSidebarItem,
  AppSidebarNav,
  AppSidebarRegularGroup,
  AppSidebarSection,
  AppSidebarSectionAddButton,
  AppSidebarSeparator,
  AppSidebarUserMenu,
  AppSidebarUserTile,
}
